<template>
    <section class="branding-editor mb-4">
        <h2 class="fs-5">Identidad del evento</h2>
        <p class="form-text">Los cambios se ven aquí al momento. Se aplican a los participantes al guardar el evento.</p>
        <div class="branding-colors">
            <label v-for="field in fields" :key="field.key" class="branding-color">
                <input type="color" :value="value[field.key]" @input="update(field.key, $event.target.value)">
                <span>{{ field.label }}<small>{{ value[field.key] }}</small></span>
            </label>
        </div>
        <label class="form-check form-switch mt-3">
            <input class="form-check-input" type="checkbox" :checked="value.animateStripes" @change="update('animateStripes', $event.target.checked)">
            <span class="form-check-label">Animar suavemente las franjas del fondo</span>
        </label>
        <div v-if="value.animateStripes" class="branding-speed">
            <label class="form-label mb-1" for="event-stripe-speed">Velocidad de las franjas</label>
            <div class="d-flex align-items-center gap-2">
                <small>Lenta</small>
                <input id="event-stripe-speed" class="form-range flex-fill" type="range" min="1" max="10" step="1" :value="value.stripeSpeed" @input="update('stripeSpeed', Number($event.target.value))">
                <small>Rápida</small>
            </div>
        </div>
        <p class="form-text">Se respeta la preferencia del dispositivo de reducir las animaciones.</p>
        <label class="form-label mt-3" for="event-logo-url">Logo del evento (URL)</label>
        <input id="event-logo-url" class="form-control" type="url" :value="modelValue.logo" placeholder="https://ejemplo.com/logo.png" @input="update('logo', $event.target.value)">
        <p v-if="modelValue.logo && !value.logo" class="text-danger small">Introduce una URL http/https o una ruta local que empiece por /.</p>
        <p class="form-text">Sin logo se muestra el nombre del evento. Acierto y error mantienen sus colores de estado.</p>
        <button class="btn btn-sm btn-outline-secondary mb-3" type="button" @click="$emit('update:modelValue', normalizeBranding(null))">Restablecer paleta de referencia</button>
        <div class="d-flex flex-wrap gap-2 mb-2">
            <label class="flex-fill">Pantalla
                <select class="form-select form-select-sm" v-model="screen">
                    <option value="title">Presentación</option><option value="activity">Pregunta</option><option value="interlude">Parada</option>
                </select>
            </label>
            <label>Dispositivo
                <select class="form-select form-select-sm" v-model="device"><option value="desktop">Escritorio</option><option value="mobile">Móvil</option></select>
            </label>
        </div>
        <div ref="previewShell" class="branding-preview-shell" :style="{ height: (device === 'mobile' ? 780 : 700) * previewScale + 'px' }">
            <iframe ref="preview" title="Vista previa del diseño del evento" :class="['branding-preview', device]" :style="{ zoom: previewScale }" :srcdoc="previewDocument" sandbox="allow-same-origin" @load="renderPreview"></iframe>
        </div>
        <p class="form-text mb-0">Vista previa con datos de ejemplo · {{ device === 'mobile' ? '390 × 780' : '1100 × 700' }} px</p>
    </section>
</template>
<script>
import { createApp, h, markRaw } from 'vue'
import EventDesignPreview from './EventDesignPreview.vue'
import { normalizeBranding } from '@/utils/event-branding'
export default {
    props: { modelValue: { type: Object, required: true }, name: String, theme: String },
    emits: ['update:modelValue'],
    data: () => ({
        screen: 'title', device: 'desktop', previewApp: null, previewWidth: 440,
        fields: [{key:'primary',label:'Principal'}, {key:'dark',label:'Fondo oscuro'}, {key:'background',label:'Fondo claro'}, {key:'surface',label:'Tarjetas'}, {key:'text',label:'Texto'}],
        previewDocument: '<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0"><div id="preview-root"></div></body></html>',
    }),
    computed: {
        value() { return normalizeBranding(this.modelValue); },
        previewScale() { return Math.min(.65, this.previewWidth / (this.device === 'mobile' ? 390 : 1100)); },
    },
    mounted() {
        // Deferred to the next frame and ignoring sub-pixel jitter: resizing the shell
        // here would otherwise re-trigger the observer in the same frame (loop warning).
        this.previewObserver = new ResizeObserver(entries => {
            const width = Math.round(entries[0].contentRect.width);
            cancelAnimationFrame(this.previewFrame);
            this.previewFrame = requestAnimationFrame(() => { if (width > 0 && Math.abs(width - this.previewWidth) > 1) this.previewWidth = width; });
        });
        this.previewObserver.observe(this.$refs.previewShell);
    },
    beforeUnmount() { this.previewApp?.unmount(); this.previewObserver?.disconnect(); cancelAnimationFrame(this.previewFrame); },
    methods: {
        normalizeBranding,
        update(key, value) { this.$emit('update:modelValue', { ...this.modelValue, [key]: value }); },
        renderPreview() {
            this.previewApp?.unmount();
            const doc = this.$refs.preview.contentDocument;
            // The actual CSS, components and media queries run at the selected viewport width.
            document.querySelectorAll('style, link[rel="stylesheet"]').forEach(style => doc.head.appendChild(style.cloneNode(true)));
            const owner = this;
            this.previewApp = markRaw(createApp({ render: () => h(EventDesignPreview, { branding: owner.value, name: owner.name, screen: owner.screen, theme: owner.theme }) }));
            this.previewApp.mount(doc.getElementById('preview-root'));
        },
    },
}
</script>
<style scoped>
.branding-editor { border-top:1px solid var(--vc-line); padding-top:20px; }
.branding-colors { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
.branding-color { display:flex; align-items:center; gap:10px; font-size:.8rem; }
.branding-color input { width:40px; height:40px; padding:2px; border:1px solid #ddd; border-radius:8px; background:white; }
.branding-speed { max-width: 360px; margin-top: 8px; }
.branding-speed small { color:#777; font-size:.7rem; }
.branding-color small { display:block; color:#777; font-size:.65rem; }
.branding-preview-shell { width:100%; overflow:hidden; background:#e8e8ed; border:1px solid #ddd; border-radius:10px; height:320px; }
.branding-preview { border:0; display:block; margin:0 auto; transform-origin:top left; width:1100px; height:700px; zoom:.4; }
.branding-preview.mobile { width:390px; height:780px; zoom:.4; margin:0 auto; }
</style>
